import React, { useState, useEffect } from 'react';
import { Plus, Search, Edit2, Trash2, Tag, Truck, Ship, FileText, Package, Warehouse, Shield, Calculator, DollarSign } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { useLanguage } from '../../context/LanguageContext';

import { baseURL } from '@/config/APIConfig';

// const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
// const API = BACKEND_URL ? `${BACKEND_URL.replace(/\/$/, '')}/api` : '/api';
const API_URL = baseURL.AiBaseURL;

const CHARGE_CATEGORIES = [
    { value: 'freight', label: 'Freight', icon: Ship, color: 'bg-blue-500' },
    { value: 'surcharge', label: 'Surcharge', icon: DollarSign, color: 'bg-purple-500' },
    { value: 'terminal', label: 'Terminal', icon: Package, color: 'bg-cyan-500' },
    { value: 'container', label: 'Container', icon: Package, color: 'bg-orange-500' },
    { value: 'trucking', label: 'Trucking', icon: Truck, color: 'bg-green-500' },
    { value: 'documentation', label: 'Documentation', icon: FileText, color: 'bg-yellow-500' },
    { value: 'customs', label: 'Customs', icon: Shield, color: 'bg-red-500' },
    { value: 'warehouse', label: 'Warehouse', icon: Warehouse, color: 'bg-indigo-500' },
    { value: 'insurance', label: 'Insurance', icon: Shield, color: 'bg-pink-500' },
    { value: 'admin', label: 'Admin', icon: Calculator, color: 'bg-slate-500' },
    { value: 'agency', label: 'Agency', icon: Tag, color: 'bg-teal-500' },
    { value: 'local', label: 'Local', icon: Tag, color: 'bg-amber-500' },
];

const UNIT_OF_MEASURES = [
    'Per Container',
    'Per BL',
    'Per Shipment',
    'Per CBM',
    'Per KG',
    'Per TEU',
    'Per Ton',
    'Lump Sum',
];

const AccountingChargeCodes = () => {
    const { t } = useLanguage();
    const [allChargeCodes, setAllChargeCodes] = useState([]); // For category counts
    const [chargeCodes, setChargeCodes] = useState([]); // For filtered display
    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterCategory, setFilterCategory] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingCode, setEditingCode] = useState(null);
    const [formData, setFormData] = useState({
        charge_code: '',
        charge_name: '',
        charge_category: 'freight',
        charge_type: '',
        description: '',
        unit_of_measure: 'Per Container',
        is_revenue: true,
        is_cost: true,
        tax_applicable: false,
        default_tax_rate: 0,
        revenue_account_id: '',
        cost_account_id: '',
        is_active: true,
        sort_order: 0
    });

    // Fetch all codes once for counts
    useEffect(() => {
        fetchAllChargeCodes();
        fetchAccounts();
    }, []);

    // Fetch filtered codes when filter changes
    useEffect(() => {
        fetchChargeCodes();
    }, [filterCategory, searchTerm]);

    const fetchAllChargeCodes = async () => {
        try {
            const response = await fetch(`${API_URL}/api/accounting/charge-codes`);
            const data = await response.json();
            setAllChargeCodes(data);
        } catch (error) {
            console.error('Error fetching all charge codes:', error);
        }
    };

    const fetchChargeCodes = async () => {
        try {
            let url = `${API_URL}/api/accounting/charge-codes?`;
            if (filterCategory) url += `category=${filterCategory}&`;
            if (searchTerm) url += `search=${searchTerm}&`;
            
            const response = await fetch(url);
            const data = await response.json();
            setChargeCodes(data);
        } catch (error) {
            console.error('Error fetching charge codes:', error);
            toast.error('Failed to load charge codes');
        } finally {
            setLoading(false);
        }
    };

    const fetchAccounts = async () => {
        try {
            const response = await fetch(`${API_URL}/api/accounting/chart-of-accounts?is_active=true`);
            const data = await response.json();
            setAccounts(data);
        } catch (error) {
            console.error('Error fetching accounts:', error);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const url = editingCode 
                ? `${API_URL}/api/accounting/charge-codes/${editingCode.id}`
                : `${API_URL}/api/accounting/charge-codes`;
            
            const response = await fetch(url, {
                method: editingCode ? 'PUT' : 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData)
            });
            
            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.detail || 'Failed to save charge code');
            }
            
            toast.success(editingCode ? 'Charge code updated!' : 'Charge code created!');
            setShowModal(false);
            setEditingCode(null);
            resetForm();
            fetchChargeCodes();
            fetchAllChargeCodes(); // Refresh counts
        } catch (error) {
            toast.error(error.message);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this charge code?')) return;
        
        try {
            const response = await fetch(`${API_URL}/api/accounting/charge-codes/${id}`, {
                method: 'DELETE'
            });
            
            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.detail || 'Failed to delete charge code');
            }
            
            toast.success('Charge code deleted!');
            fetchChargeCodes();
            fetchAllChargeCodes(); // Refresh counts
        } catch (error) {
            toast.error(error.message);
        }
    };

    const openEditModal = (code) => {
        setEditingCode(code);
        setFormData({
            charge_code: code.charge_code || '',
            charge_name: code.charge_name || '',
            charge_category: code.charge_category || 'freight',
            charge_type: code.charge_type || '',
            description: code.description || '',
            unit_of_measure: code.unit_of_measure || 'Per Container',
            is_revenue: code.is_revenue ?? true,
            is_cost: code.is_cost ?? true,
            tax_applicable: code.tax_applicable || false,
            default_tax_rate: code.default_tax_rate || 0,
            revenue_account_id: code.revenue_account_id || '',
            cost_account_id: code.cost_account_id || '',
            is_active: code.is_active ?? true,
            sort_order: code.sort_order || 0
        });
        setShowModal(true);
    };

    const resetForm = () => {
        setFormData({
            charge_code: '',
            charge_name: '',
            charge_category: 'freight',
            charge_type: '',
            description: '',
            unit_of_measure: 'Per Container',
            is_revenue: true,
            is_cost: true,
            tax_applicable: false,
            default_tax_rate: 0,
            revenue_account_id: '',
            cost_account_id: '',
            is_active: true,
            sort_order: 0
        });
    };

    const getCategoryCounts = () => {
        const counts = {};
        CHARGE_CATEGORIES.forEach(cat => {
            counts[cat.value] = allChargeCodes.filter(c => c.charge_category === cat.value).length;
        });
        return counts;
    };

    const categoryCounts = getCategoryCounts();

    const getCategoryInfo = (category) => CHARGE_CATEGORIES.find(c => c.value === category) || CHARGE_CATEGORIES[0];

    const getAccountName = (accountId) => {
        const account = accounts.find(a => a.id === accountId);
        return account ? `${account.account_code} - ${account.account_name}` : '-';
    };

    // Filter accounts by type
    const revenueAccounts = accounts.filter(a => a.account_type === 'income');
    const expenseAccounts = accounts.filter(a => a.account_type === 'expense' || a.account_type === 'cogs');

    return (
        <div className="p-6 space-y-6 bg-slate-50 dark:bg-slate-900 min-h-screen">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                        <Tag className="w-7 h-7 text-purple-500" />
                        {t('chargeCodes')}
                    </h1>
                    <p className="text-slate-500 dark:text-slate-400 mt-1">
                        {t('manageChargeCodes')}
                    </p>
                </div>
                <Button 
                    onClick={() => { resetForm(); setEditingCode(null); setShowModal(true); }}
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                    data-testid="add-charge-code-btn"
                >
                    <Plus className="w-4 h-4 mr-2" />
                    {t('addChargeCode')}
                </Button>
            </div>

            {/* Category Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {CHARGE_CATEGORIES.slice(0, 6).map((category) => {
                    const Icon = category.icon;
                    return (
                        <div 
                            key={category.value}
                            className={`bg-white dark:bg-slate-800 rounded-xl p-4 shadow-sm border-l-4 cursor-pointer hover:shadow-md transition-shadow ${
                                filterCategory === category.value ? 'ring-2 ring-purple-500' : ''
                            }`}
                            style={{ borderLeftColor: category.color.replace('bg-', '#').replace('-500', '') }}
                            onClick={() => setFilterCategory(filterCategory === category.value ? '' : category.value)}
                        >
                            <div className="flex items-center justify-between mb-2">
                                <div className={`w-8 h-8 rounded-lg ${category.color} bg-opacity-20 flex items-center justify-center`}>
                                    <Icon className={`w-4 h-4 ${category.color.replace('bg-', 'text-')}`} />
                                </div>
                                <span className="text-lg font-bold text-slate-900 dark:text-white">
                                    {categoryCounts[category.value] || 0}
                                </span>
                            </div>
                            <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{category.label}</p>
                        </div>
                    );
                })}
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-4 items-center bg-white dark:bg-slate-800 rounded-xl p-4 shadow-sm">
                <div className="flex-1 min-w-[200px]">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                            placeholder={t('searchChargeCodes')}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-10"
                            data-testid="search-charge-codes"
                        />
                    </div>
                </div>
                <select
                    value={filterCategory}
                    onChange={(e) => setFilterCategory(e.target.value)}
                    className="px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                >
                    <option value="">All Categories</option>
                    {CHARGE_CATEGORIES.map(cat => (
                        <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                </select>
            </div>

            {/* Charge Codes Table */}
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="bg-slate-100 dark:bg-slate-700">
                                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase">Code</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase">Name</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase">Category</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase">Unit</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase">Revenue GL</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase">Cost GL</th>
                                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase">AR/AP</th>
                                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase">Tax</th>
                                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                            {loading ? (
                                <tr>
                                    <td colSpan={9} className="px-4 py-8 text-center text-slate-500">
                                        {t('loading')}
                                    </td>
                                </tr>
                            ) : chargeCodes.length === 0 ? (
                                <tr>
                                    <td colSpan={9} className="px-4 py-8 text-center text-slate-500">
                                        {t('noChargeCodesFound')}
                                    </td>
                                </tr>
                            ) : (
                                chargeCodes.map((code) => {
                                    const categoryInfo = getCategoryInfo(code.charge_category);
                                    return (
                                        <tr key={code.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/50">
                                            <td className="px-4 py-3 text-sm font-mono font-medium text-slate-900 dark:text-white">
                                                {code.charge_code}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-slate-900 dark:text-white">
                                                {code.charge_name}
                                            </td>
                                            <td className="px-4 py-3">
                                                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${categoryInfo.color} bg-opacity-20 ${categoryInfo.color.replace('bg-', 'text-')}`}>
                                                    {categoryInfo.label}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">
                                                {code.unit_of_measure || '-'}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">
                                                {getAccountName(code.revenue_account_id)}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">
                                                {getAccountName(code.cost_account_id)}
                                            </td>
                                            <td className="px-4 py-3 text-center">
                                                <div className="flex items-center justify-center gap-1">
                                                    {code.is_revenue && (
                                                        <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                                                            AR
                                                        </span>
                                                    )}
                                                    {code.is_cost && (
                                                        <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
                                                            AP
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 text-center text-sm text-slate-600 dark:text-slate-400">
                                                {code.tax_applicable ? `${code.default_tax_rate}%` : '-'}
                                            </td>
                                            <td className="px-4 py-3 text-center">
                                                <div className="flex items-center justify-center gap-2">
                                                    <button
                                                        onClick={() => openEditModal(code)}
                                                        className="p-1.5 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 text-blue-600"
                                                        title="Edit"
                                                    >
                                                        <Edit2 className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(code.id)}
                                                        className="p-1.5 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600"
                                                        title="Delete"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-slate-800 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b border-slate-200 dark:border-slate-700">
                            <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                                {editingCode ? t('editChargeCode') : t('addChargeCode')}
                            </h2>
                        </div>
                        <form onSubmit={handleSubmit} className="p-6 space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                                        Charge Code <span className="text-red-500">*</span>
                                    </label>
                                    <Input
                                        value={formData.charge_code}
                                        onChange={(e) => setFormData({...formData, charge_code: e.target.value.toUpperCase()})}
                                        required
                                        placeholder="e.g., OFR"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                                        Charge Name <span className="text-red-500">*</span>
                                    </label>
                                    <Input
                                        value={formData.charge_name}
                                        onChange={(e) => setFormData({...formData, charge_name: e.target.value})}
                                        required
                                        placeholder="e.g., Ocean Freight"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                                        Category <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        value={formData.charge_category}
                                        onChange={(e) => setFormData({...formData, charge_category: e.target.value})}
                                        className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800"
                                        required
                                    >
                                        {CHARGE_CATEGORIES.map(cat => (
                                            <option key={cat.value} value={cat.value}>{cat.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                                        Unit of Measure
                                    </label>
                                    <select
                                        value={formData.unit_of_measure}
                                        onChange={(e) => setFormData({...formData, unit_of_measure: e.target.value})}
                                        className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800"
                                    >
                                        {UNIT_OF_MEASURES.map(unit => (
                                            <option key={unit} value={unit}>{unit}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                                    Description
                                </label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                                    className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800"
                                    rows={2}
                                    placeholder="Optional description..."
                                />
                            </div>

                            {/* GL Account Mapping */}
                            <div className="p-4 bg-slate-50 dark:bg-slate-900 rounded-lg space-y-4">
                                <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300">GL Account Mapping</h3>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                                            Revenue Account (AR)
                                        </label>
                                        <select
                                            value={formData.revenue_account_id}
                                            onChange={(e) => setFormData({...formData, revenue_account_id: e.target.value})}
                                            className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm"
                                        >
                                            <option value="">Select account...</option>
                                            {revenueAccounts.map(acc => (
                                                <option key={acc.id} value={acc.id}>
                                                    {acc.account_code} - {acc.account_name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                                            Cost Account (AP)
                                        </label>
                                        <select
                                            value={formData.cost_account_id}
                                            onChange={(e) => setFormData({...formData, cost_account_id: e.target.value})}
                                            className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm"
                                        >
                                            <option value="">Select account...</option>
                                            {expenseAccounts.map(acc => (
                                                <option key={acc.id} value={acc.id}>
                                                    {acc.account_code} - {acc.account_name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>

                            <div className="flex items-center gap-6">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={formData.is_revenue}
                                        onChange={(e) => setFormData({...formData, is_revenue: e.target.checked})}
                                        className="w-4 h-4 rounded border-slate-300"
                                    />
                                    <span className="text-sm text-slate-700 dark:text-slate-300">Revenue (AR)</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={formData.is_cost}
                                        onChange={(e) => setFormData({...formData, is_cost: e.target.checked})}
                                        className="w-4 h-4 rounded border-slate-300"
                                    />
                                    <span className="text-sm text-slate-700 dark:text-slate-300">Cost (AP)</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={formData.tax_applicable}
                                        onChange={(e) => setFormData({...formData, tax_applicable: e.target.checked})}
                                        className="w-4 h-4 rounded border-slate-300"
                                    />
                                    <span className="text-sm text-slate-700 dark:text-slate-300">Tax Applicable</span>
                                </label>
                            </div>

                            {formData.tax_applicable && (
                                <div className="w-32">
                                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                                        Default Tax Rate (%)
                                    </label>
                                    <Input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        max="100"
                                        value={formData.default_tax_rate}
                                        onChange={(e) => setFormData({...formData, default_tax_rate: parseFloat(e.target.value) || 0})}
                                    />
                                </div>
                            )}

                            <div className="flex justify-end gap-3 pt-4 border-t border-slate-200 dark:border-slate-700">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => { setShowModal(false); setEditingCode(null); }}
                                >
                                    {t('cancel')}
                                </Button>
                                <Button type="submit" className="bg-purple-600 hover:bg-purple-700 text-white">
                                    {editingCode ? t('update') : t('create')}
                                </Button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AccountingChargeCodes;
